import axios, { type AxiosError } from "axios";
import {
  acquireAccessTokenSilentOrThrow,
  API_SCOPES,
  isInteractionRequired,
  msalInstance,
} from "@/auth/msal.ts";
import { toast } from "sonner";
import { isDev } from "@/lib/utils.ts";

type BusinessError = {
  code: string;
  message: string;
};

declare module "axios" {
  export interface AxiosRequestConfig {
    msScopes?: string[];
    msSkipAuth?: boolean;
    _retry?: boolean;
  }
}

const BACKEND_BASE_URL = import.meta.env.VITE_BACKEND_URL;
const apiBaseOrigin = new URL(BACKEND_BASE_URL).origin;

let reauthScheduled = false;

export type ListParams = {
  page?: number;
  size?: number;
  sort: string[];
};

export function toSearchParams(p: ListParams) {
  const sp = new URLSearchParams();
  if (p.page !== undefined) sp.set("page", String(p.page));
  if (p.size !== undefined) sp.set("size", String(p.size));
  (p.sort ?? []).forEach((s) => sp.append("sort", s));
  return sp.toString();
}

export function isAxiosCanceled(e: unknown) {
  return axios.isCancel(e);
}

export const api = axios.create({ baseURL: BACKEND_BASE_URL });

api.interceptors.request.use(async (config) => {
  if (config.msSkipAuth) return config;

  const url = new URL(config.url ?? "", BACKEND_BASE_URL);
  const isOwnApi = url.origin === apiBaseOrigin;
  if (!isOwnApi) return config;

  try {
    const token = await acquireAccessTokenSilentOrThrow(
      config.msScopes ?? API_SCOPES,
    );
    config.headers = config.headers ?? {};
    (config.headers as any).Authorization = `Bearer ${token}`;
    return config;
  } catch (err) {
    if (isInteractionRequired(err) && !reauthScheduled) {
      reauthScheduled = true;
      void msalInstance.acquireTokenRedirect({
        scopes: config.msScopes ?? API_SCOPES,
      });
      return Promise.reject(new axios.Cancel("Auth redirect scheduled"));
    }
    return Promise.reject(err);
  }
});

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    if (axios.isCancel(error)) return Promise.reject(error);

    const axiosError = error as AxiosError<any>;
    const { response, config } = axiosError || {};

    if (response?.status === 401 && config && !reauthScheduled) {
      reauthScheduled = true;
      void msalInstance.acquireTokenRedirect({
        scopes: config.msScopes ?? API_SCOPES,
      });
      return Promise.reject(new axios.Cancel("Auth redirect scheduled on 401"));
    }

    if (response) {
      const data = response.data as BusinessError;
      const code = data.code;
      const message = data.message;

      if (message || code) {
        toast.error(
          `${code ? `${code}: ` : ""}${message ?? "Unbekannter Fehler"}`,
        );
      } else {
        toast.error(
          `Fehler ${response.status}: ${
            response.statusText || "Unbekannter Fehler"
          }`,
        );
      }
    } else {
      if (isDev) {
        toast.error(error.message);
      } else {
        toast.error("Netzwerkfehler oder Server nicht erreichbar.");
      }
    }

    return Promise.reject(error);
  },
);
